/**
 * Two small per-browser facts, kept in localStorage.
 *
 * Every access is wrapped: Safari in private mode throws on localStorage, and neither of these
 * is worth failing a render over — same treatment as `pwa-install-dismissed`.
 */

const DEVICE_ID_KEY = "push-device-id";

/**
 * The `PushDevice.id` the server returned when this browser subscribed.
 *
 * This is how the UI recognises "this device" in the device list. Comparing endpoints would
 * work too, but the endpoint is a bearer URL and deliberately never leaves the server.
 */
export function readDeviceId(): number | null {
  try {
    const raw = window.localStorage.getItem(DEVICE_ID_KEY);
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isInteger(parsed) ? parsed : null;
  } catch {
    // Not being able to remember which device we are is survivable — the toggle still works.
    return null;
  }
}

export function writeDeviceId(id: number): void {
  try {
    window.localStorage.setItem(DEVICE_ID_KEY, String(id));
  } catch {
    // Nothing to do.
  }
}

export function clearDeviceId(): void {
  try {
    window.localStorage.removeItem(DEVICE_ID_KEY);
  } catch {
    // Nothing to do.
  }
}

/** Before the flag was per user it was one global key; it is dropped, never migrated. */
const LEGACY_PROMPT_DISMISSED_KEY = "push-prompt-dismissed";

function promptDismissedKey(userId: string): string {
  return `${LEGACY_PROMPT_DISMISSED_KEY}:${userId}`;
}

/**
 * Whether this user closed the "Benachrichtigungen einschalten" banner on this browser.
 *
 * Per user, because a shared device has several: one worker closing the banner must not hide
 * it from the next one who signs in.
 */
export function readPromptDismissed(userId: string): boolean {
  try {
    window.localStorage.removeItem(LEGACY_PROMPT_DISMISSED_KEY);
    return window.localStorage.getItem(promptDismissedKey(userId)) === "true";
  } catch {
    // Failing to remember a dismissal is better than not rendering.
    return false;
  }
}

export function dismissPrompt(userId: string): void {
  try {
    window.localStorage.setItem(promptDismissedKey(userId), "true");
  } catch {
    // The banner just comes back next session.
  }
}
